import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';

import { Role } from '@ndvr/auth-api';

import { CustomerApi } from 'API';
import { useUserHasRole } from 'views/admin/dev-queries';

import { OnboardingHousehold } from './useOnboardingHousehold';

export const useFidelitySmaApplications = (household: Pick<OnboardingHousehold, 'accounts'>) => {
    const { data: ssnAccess, isPending: checkingAccess } = useUserHasRole(Role.SSN_ACCESS);
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);
    const personal = useQuery({
        queryKey: ['onboarding', 'fidelity-personal-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getSmaPersonalApplications({ accountGUIDs }))[0],
        enabled: !!ssnAccess && accountGUIDs.length > 0,
    });
    const trust = useQuery({
        queryKey: ['onboarding', 'fidelity-trust-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getSmaTrustApplications({ accountGUIDs }))[0],
        enabled: !!ssnAccess && accountGUIDs.length > 0,
    });
    const business = useQuery({
        queryKey: ['onboarding', 'fidelity-business-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getSmaBusinessApplications({ accountGUIDs }))[0],
        enabled: !!ssnAccess && accountGUIDs.length > 0,
    });
    const retirement = useQuery({
        queryKey: ['onboarding', 'fidelity-retirement-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getSmaRetirementApplications({ accountGUIDs }))[0],
        enabled: !!ssnAccess && accountGUIDs.length > 0,
    });
    const ira = useQuery({
        // Saved account edits change this key so a previous grouping cannot survive an election change.
        queryKey: ['onboarding', 'fidelity-applications', household.accounts],
        queryFn: async () => (await CustomerApi({ hideError: true }).getSmaIraApplications({ accountGUIDs }))[0] ?? [],
        enabled: !!ssnAccess && accountGUIDs.length > 0,
    });
    return { personal, trust, business, retirement, ira, ssnAccess: !!ssnAccess, checkingAccess };
};

export type FidelitySmaSectionProps<K extends 'personal' | 'trust' | 'business' | 'retirement' | 'ira'> = {
    household: Pick<OnboardingHousehold, 'accounts'>;
    applications: ReturnType<typeof useFidelitySmaApplications>[K];
    action: (guid: string) => ReactNode;
    ssnAccess: boolean;
    checkingAccess: boolean;
};
